import { EventEmitter, Injectable, Sanitizer } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { UserContactStatus } from 'node_js/models/enums/user-contact-status';
import { UserContact } from 'node_js/models/tables/user_contact';
import { arrayBufferToBlob, bufferObjectToArrayBuffer, connLink, convertLocalDateToUTCMySQLString, convertUTCStringToLocalDate, getCurrentDateForDB, mergeBufferArray, presentSnackBar, updateObjKeepingRef } from 'src/global';
import { UserAuthService } from './user-auth.service';
import { HttpClient } from '@angular/common/http';
import { UserContactRequest } from 'node_js/models/tables/user_contact_request';
import { FailedResponse, GenericErrorCode } from 'node_js/models/failed-response.model';
import { SnackBarColor } from 'node_js/models/enums/snackbar-color';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserContactRequestInfo } from 'node_js/models/business/user_contact_request_info';
import { LanguageEnum } from 'node_js/models/enums/language';
import { LabComplete } from 'node_js/models/business/lab/lab-complete';
import { GadgetComplete } from 'node_js/models/business/lab/gadget-complete';
import { ComponentComplete } from 'node_js/models/business/lab/component-complete';
import { LabUserComplete } from 'node_js/models/business/lab/lab-user-complete';
import { LabUser } from 'node_js/models/tables/lab_user';
import { GadgetUser } from 'node_js/models/tables/gadget_user';
import { RoleEnum } from 'node_js/models/enums/role';
import { LabUserRequestComplete } from 'node_js/models/business/lab-user-request-complete';
import { LabUserCombo } from 'node_js/models/business/lab/lab-user-combo';
import { DateFrequencyEnum } from 'node_js/models/enums/date-frequency';
import { ComponentSchedule } from 'node_js/models/tables/component_schedule';
import { ComponentTriggerComplete } from 'node_js/models/business/lab/component-trigger-complete';
import { BehaviorSubject, Subject } from 'rxjs';
import { ComponentSettingsReturn } from 'node_js/models/business/component-settings-return';
import { ComponentTriggerEffectUser } from 'node_js/models/tables/component_trigger_effect_user';
import { ComponentTriggerEffect } from 'node_js/models/tables/component_trigger_effect';
import { Component } from 'node_js/models/tables/component';
import { LiveUpdatesService } from './live-updates.service';
import { ioLabSettings } from 'node_js/models/business/lab/io-lab-settings';
import { MainSliderSection } from '../pages/dashboard/dashboard.page';
import { StateService } from './state.service';
import { LabControllerComplete } from 'node_js/models/business/lab/lab-controller-complete';
import { LabController } from 'node_js/models/tables/lab_controller';
import { LabControllerEditPermissionEnum, LabControllerUsePermissionEnum } from 'node_js/models/enums/lab-controller-permission';
import { Lab } from 'node_js/models/tables/lab';
import { DomSanitizer } from '@angular/platform-browser';
import { FileUploadComplete } from 'node_js/models/business/lab/file-upload-complete';
import { FileUploadService } from './file-upload.service';
import { GadgetService } from './gadget.service';
import { IoRoomHeaderEnum } from 'node_js/models/enums/io-room-header';
import { ComponentValueHistory } from 'node_js/models/tables/component_value_history';
import { ComponentValueHistoryPage } from 'node_js/models/business/component-value-history-page';



@Injectable({
  providedIn: 'root'
})
export class LabService {

  currentSelectedLab: LabComplete;
  currentSelectedGadget: GadgetComplete;
  currentSelectedComponent: ComponentComplete;

  //Subscriptions
  currentSelectedComponentChanged: Subject<any>;
  currentSelectedComponentValueChanged: Subject<any>;
  currentSelectedComponentScheduleChanged: Subject<any>;
  currentSelectedLabUserCreated: Subject<any>;
  currentSelectedLabUserSettingsChanged: Subject<any>;
  currentSelectedLabSettingsChanged: Subject<any>;
  currentSelectedGadgetSettingsChanged: Subject<any>;
  currentSelectedLabUserRequestDeleted: Subject<any>;
  currentSelectedLabUserRemoved: Subject<any>;
  componentChanged: Subject<any>;
  dataRefreshed$ = new Subject<void>();

  labControllerCompleteChanged: Subject<any>;



  constructor(private http: HttpClient, private matSnackBar: MatSnackBar, private liveUpdatesService: LiveUpdatesService,
    private stateService: StateService, private fileUploadService: FileUploadService
  ) {
    this.currentSelectedComponentChanged = new Subject();
    this.currentSelectedComponentValueChanged = new Subject();
    this.currentSelectedComponentScheduleChanged = new Subject();
    this.currentSelectedLabUserCreated = new Subject();
    this.currentSelectedLabUserSettingsChanged = new Subject();
    this.currentSelectedLabSettingsChanged = new Subject();
    this.currentSelectedGadgetSettingsChanged = new Subject();
    this.currentSelectedLabUserRequestDeleted = new Subject();
    this.currentSelectedLabUserRemoved = new Subject();
    this.labControllerCompleteChanged = new Subject();
    this.componentChanged = new Subject();
  }

  enterLab(labComplete: LabComplete, userAuthService?: UserAuthService) {
    try {
      console.log('[LabService] enterLab called with lab:', labComplete?.labLanguage?.name || labComplete?.lab?.id || 'null');

      // Add comprehensive null/undefined checks
      if (!labComplete) {
        console.error('[LabService] enterLab called with null/undefined labComplete');
        return;
      }

      if (!labComplete.lab || !labComplete.lab.id) {
        console.error('[LabService] enterLab called with invalid labComplete object (missing lab or lab.id):', labComplete);
        return;
      }

      // Safety check: Verify user is actually a member of this lab (if userAuthService is provided)
      if (userAuthService && userAuthService.loggedIn && userAuthService.currentUserComplete) {
        const userIsLabMember = userAuthService.currentUserComplete.labsComplete.some(
          lab => lab.lab.id === labComplete?.lab?.id
        );

        if (!userIsLabMember) {
          console.error('[LabService] User is not a member of lab:', labComplete?.labLanguage?.name || labComplete?.lab?.id);
          console.error('[LabService] Available labs:', userAuthService.currentUserComplete.labsComplete.map(l => l.labLanguage?.name || l.lab.id));
          return;
        }
      }

      try {
        this.currentSelectedLab = labComplete;

        console.log('[LabService] currentSelectedLab set to:', this.currentSelectedLab?.labLanguage?.name || this.currentSelectedLab?.lab?.id || 'null');
        // No need to join room here - user should already be in all lab rooms
        // Room joining happens on login or when accepting new lab invitations
      } catch (error) {
        console.error('[LabService] Error setting currentSelectedLab:', error);
      }
    } catch (error) {
      console.error('[LabService] Unexpected error in enterLab:', error);
      if (error instanceof Error) {
        console.error('[LabService] Error stack:', error.stack);
      }
    }
  }

  leaveLab() {
    // Don't leave the socket room - just clear the current selected lab
    // User stays in room to receive updates for all their labs
    this.currentSelectedLab = null;
  }

  refreshSelectedReferences(userAuthService: UserAuthService): void {
    const labsComplete = userAuthService.currentUserComplete?.labsComplete;
    if (!labsComplete) return;

    if (this.currentSelectedLab) {
      const freshLab = labsComplete.find(l => l.lab.id === this.currentSelectedLab.lab.id) ?? null;
      this.currentSelectedLab = freshLab;

      if (freshLab && this.currentSelectedGadget) {
        const freshGadget = freshLab.gadgetsComplete.find(g => g.gadget.id === this.currentSelectedGadget.gadget.id) ?? null;
        this.currentSelectedGadget = freshGadget;

        if (freshGadget && this.currentSelectedComponent) {
          const freshComponent = freshGadget.componentsComplete.find(c => c.component.id === this.currentSelectedComponent.component.id) ?? null;
          this.currentSelectedComponent = freshComponent;
        }
      }
    }
  }

  cloneLab(userAuthService: UserAuthService, labId: string) {
    return new Promise<LabComplete>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();
      this.http.post(connLink + "/cloneLab", { uid: userAuthService.currentUserComplete.user.authUid, idToken, labId }).subscribe(
        (response: any) => {
          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          reject();
        }
      )
    })
  }

  updateLabSettings(userAuthService: UserAuthService, labComplete: LabComplete) {
    return new Promise<void>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();


      const formData = new FormData();
      formData.append("labComplete", JSON.stringify(labComplete));
      formData.append("uid", userAuthService.currentUserComplete.user.authUid);
      formData.append("idToken", idToken.toString());

      if (labComplete.iconFileUploadComplete?.file) {
        formData.append("files", labComplete.iconFileUploadComplete.file, labComplete.iconFileUploadComplete.file.name);
      }


      this.http.post(connLink + "/updateLabSettings", formData, {}).subscribe(
        (response: any) => {
          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          reject();
        }
      )
    })
  }

  async updateLabSettingsLocally(ioLabSettings: ioLabSettings, userAuthService: UserAuthService) {
    console.log("updating lab settings locally", ioLabSettings);
    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];
      if (labComplete.lab.id == ioLabSettings.lab.id) {
        console.log("lab complete", JSON.stringify(ioLabSettings.iconFileUploadComplete), labComplete, labComplete.iconFileUploadComplete?.srcUrl);
        // Only revoke the old URL if it exists and is a blob URL (check type first!)
        if (labComplete.iconFileUploadComplete?.srcUrl && typeof labComplete.iconFileUploadComplete.srcUrl === 'string' && labComplete.iconFileUploadComplete.srcUrl.startsWith('blob:')) {
          console.log("revoking old image URL")
          URL.revokeObjectURL(labComplete.iconFileUploadComplete.srcUrl);
        }

        if (ioLabSettings.iconFileUploadComplete?.srcUrl) {
          console.log("updated image")
        }
        else {
          console.log("couldn't update image")
        }

        labComplete.iconFileUploadComplete = ioLabSettings.iconFileUploadComplete;

        this.convertLabImageBuffersToBlob(labComplete, true);

        // Update the entire lab object with the updated data from the server
        labComplete.lab = { ...labComplete.lab, ...ioLabSettings.lab };
        labComplete.labLanguage = ioLabSettings.labLanguage;

        // Update currentSelectedLab if it's the same lab
        if (this.currentSelectedLab && this.currentSelectedLab.lab.id === ioLabSettings.lab.id) {
          this.currentSelectedLab = labComplete;
        }

        this.currentSelectedLabSettingsChanged.next(null);

      }
    }
  }

  // When getting data from a websocket, the buffer might already be an ArrayBuffer.
  // The service now robustly checks the type at runtime, so we can safely pass data through.
  convertLabImageBuffersToBlob(labComplete: LabComplete, skipArrayBufferConversion: boolean = false) {
    if (labComplete.iconFileUploadComplete) {
      if (labComplete.iconFileUploadComplete.buffer) {
        this.fileUploadService.convertFileUploadCompleteToBlob(labComplete.iconFileUploadComplete, skipArrayBufferConversion);
      } else if (labComplete.iconFileUploadComplete.fileUpload?.filePathCode) {
        // Fallback to remote URL if no buffer is present (lightweight mode)
        let filePathCode = labComplete.iconFileUploadComplete.fileUpload.filePathCode;
        labComplete.iconFileUploadComplete.srcUrl = `${connLink}/getFileFromGetRequest/${filePathCode}`;
      }
    }
  }


  getComponentTriggerFromComponentComplete(componentComplete: ComponentComplete) {
    let triggersComplete: ComponentTriggerComplete[] = [];
    for (let i = 0; i < componentComplete.componentValueChangedEventsComplete.length; i++) {
      let componentValueChangedEventComplete = componentComplete.componentValueChangedEventsComplete[i];
      triggersComplete.push(componentValueChangedEventComplete.componentTriggerComplete)
    }
    for (let j = 0; j < componentComplete.componentSchedulesComplete.length; j++) {
      let componentScheduleComplete = componentComplete.componentSchedulesComplete[j];
      triggersComplete.push(componentScheduleComplete.componentTriggerComplete);
    }
    return triggersComplete
  }

  createNewLabUserLocally(labUserComplete: LabUserComplete, userAuthService: UserAuthService, gadgetService: GadgetService) {
    console.log('[LabService] createNewLabUserLocally called for lab:', labUserComplete.labUser.labId);
    console.log('[LabService] Lab user ID:', labUserComplete.labUser.id, 'userId:', labUserComplete.labUser.userId);

    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];

      if (labComplete.lab.id == labUserComplete.labUser.labId) {
        // Check if this user already exists (by userId+labId since ID might be wrong/temporary)
        const existingUser = labComplete.labUsersComplete.find(
          existing => existing.labUser.userId === labUserComplete.labUser.userId &&
            existing.labUser.labId === labUserComplete.labUser.labId
        );

        if (existingUser) {
          console.log('[LabService] Lab user already exists, updating instead of adding');
          console.log('[LabService] Existing ID:', existingUser.labUser.id, 'New ID:', labUserComplete.labUser.id);

          // Update the existing user with new data (but keep the existing ID if it's different)
          // This handles the case where the live update has a wrong/temporary ID
          if (existingUser.labUser.id !== labUserComplete.labUser.id) {
            console.log('[LabService] ID mismatch detected - keeping existing ID:', existingUser.labUser.id);
            // Keep the existing ID but update other fields
            const updatedLabUser = { ...labUserComplete.labUser, id: existingUser.labUser.id };
            const updatedLabUserComplete = { ...labUserComplete, labUser: updatedLabUser };

            // Update the existing entry
            const index = labComplete.labUsersComplete.indexOf(existingUser);
            labComplete.labUsersComplete[index] = updatedLabUserComplete;

            // Emit with the corrected data
            this.currentSelectedLabUserCreated.next(updatedLabUserComplete);
          } else {
            // IDs match, just update the existing entry
            const index = labComplete.labUsersComplete.indexOf(existingUser);
            labComplete.labUsersComplete[index] = labUserComplete;
            this.currentSelectedLabUserCreated.next(labUserComplete);
          }
        } else {
          console.log('[LabService] Found matching lab, adding new lab user');
          labComplete.labUsersComplete.push(labUserComplete);

          // Always emit the event so all components listening can update
          console.log('[LabService] Emitting currentSelectedLabUserCreated event');
          this.currentSelectedLabUserCreated.next(labUserComplete);
        }

        // Apply any gadget users bundled with this lab user (auto-created by server)
        if (labUserComplete.gadgetUsersComplete && labUserComplete.gadgetUsersComplete.length > 0) {
          gadgetService.upsertGadgetUsersCompleteLocally(labUserComplete.gadgetUsersComplete, userAuthService);
        }

        break;
      }
    }
  }

  updateComponentSchedulesLocally(updatedComponentSchedules: ComponentSchedule[], userAuthService: UserAuthService) {
    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];

      //Loop through gadgets
      for (let j = 0; j < labComplete.gadgetsComplete.length; j++) {
        let gadgetComplete = labComplete.gadgetsComplete[j];
        //Loop through components
        for (let k = 0; k < gadgetComplete.componentsComplete.length; k++) {
          let componentComplete = gadgetComplete.componentsComplete[k];

          let isCurrentComp = false;
          let currentCompSchedules: ComponentSchedule[] = []
          for (let l = 0; l < componentComplete.componentSchedulesComplete.length; l++) {
            let componentScheduleComplete = componentComplete.componentSchedulesComplete[l];

            for (let m = 0; m < updatedComponentSchedules.length; m++) {
              let updatedComponentSchedule = updatedComponentSchedules[m];

              if (componentScheduleComplete.componentSchedule.id == updatedComponentSchedule.id) {
                componentScheduleComplete.componentSchedule.nextDate = updatedComponentSchedule.nextDate;
                //Update current selected comp
                if (this.currentSelectedComponent && this.currentSelectedComponent.component.id == updatedComponentSchedule.componentId) {
                  isCurrentComp = true;
                  currentCompSchedules.push(updatedComponentSchedule);
                }
              }
            }

          }
          if (isCurrentComp) {
            this.currentSelectedComponentScheduleChanged.next(currentCompSchedules);
          }

        }
      }
    }
  }

  updateComponentValueLocally(updatedComponent: Component, userAuthService: UserAuthService) {
    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];


      //Loop through gadgets
      for (let j = 0; j < labComplete.gadgetsComplete.length; j++) {
        let gadgetComplete = labComplete.gadgetsComplete[j];
        //Loop through components
        for (let k = 0; k < gadgetComplete.componentsComplete.length; k++) {
          let componentComplete = gadgetComplete.componentsComplete[k];

          if (componentComplete.component.id == updatedComponent.id) {
            this.updateComponentLocallyWhenValueChanged(componentComplete.component, updatedComponent);
            //Update current selected comp
            if (this.currentSelectedComponent && this.currentSelectedComponent.component.id == updatedComponent.id) {
              this.currentSelectedComponentValueChanged.next(this.currentSelectedComponent.component);
            }
            this.componentChanged.next(componentComplete);
          }
        }
      }
    }
  }

  updateComponentSettingsLocally(componentSettingsReturn: ComponentSettingsReturn, userAuthService: UserAuthService, skipArrayBufferConversion: boolean) {
    let updatedComponentComplete = componentSettingsReturn.componentComplete;
    let updatedTriggerEffects = componentSettingsReturn.componentTriggerEffects;
    this.convertComponentImageBuffersToBlob(updatedComponentComplete, skipArrayBufferConversion);
    let iconFileUploadComplete = updatedComponentComplete.iconFileUploadComplete

    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];

      let breakLabsLoop = false;

      //Loop through gadgets
      for (let j = 0; j < labComplete.gadgetsComplete.length; j++) {
        let gadgetComplete = labComplete.gadgetsComplete[j];

        if (updatedComponentComplete.component.gadgetId == gadgetComplete.gadget.id) {
          let foundComp: boolean = false;
          //Loop through components
          for (let k = 0; k < gadgetComplete.componentsComplete.length; k++) {
            let componentComplete = gadgetComplete.componentsComplete[k];

            if (componentComplete.component.id == updatedComponentComplete.component.id) {

              if (componentComplete.iconFileUploadComplete && componentComplete.iconFileUploadComplete.srcUrl) {
                URL.revokeObjectURL(componentComplete.iconFileUploadComplete.srcUrl);
              }

              //updateObjKeepingRef(componentComplete,updatedComponentComplete);
              labComplete.gadgetsComplete[j].componentsComplete[k] = updatedComponentComplete;
              componentComplete.iconFileUploadComplete = iconFileUploadComplete;

              foundComp = true;

              //Update current selected comp
              if (this.currentSelectedComponent && this.currentSelectedComponent.component.id == updatedComponentComplete.component.id) {
                this.currentSelectedComponent = gadgetComplete.componentsComplete[k];
                this.currentSelectedComponentChanged.next(componentComplete);
              }

            }
            let componentTriggers = this.getComponentTriggerFromComponentComplete(componentComplete);
            for (let l = 0; l < componentTriggers.length; l++) {
              let componentTrigger = componentTriggers[l];
              for (let m = 0; m < componentTrigger.componentTriggerEffectsComplete.length; m++) {
                let componentTriggerEffectComplete = componentTrigger.componentTriggerEffectsComplete[m];
                for (let n = 0; n < updatedTriggerEffects.length; n++) {
                  let updatedTriggerEffect = updatedTriggerEffects[n];
                  if (updatedTriggerEffect.id == componentTriggerEffectComplete.componentTriggerEffect.id) {
                    updateObjKeepingRef(componentTriggerEffectComplete.componentTriggerEffect, updatedTriggerEffect);
                    updatedTriggerEffects.splice(n, 1);
                    break;
                  }
                }
              }
            }
          }
          if (foundComp == false) {
            gadgetComplete.componentsComplete.push(updatedComponentComplete);
          }
          break;
        }

      }

      if (breakLabsLoop) {
        break;
      }
    }
    this.componentChanged.next(updatedComponentComplete);

  }

  // When getting data from a websocket, the buffer might already be an ArrayBuffer.
  // The service now robustly checks the type at runtime, so we can safely pass data through.
  convertComponentImageBuffersToBlob(componentComplete: ComponentComplete, skipArrayBufferConversion: boolean = false) {
    if (componentComplete.iconFileUploadComplete) {
      if (componentComplete.iconFileUploadComplete.buffer) {
        this.fileUploadService.convertFileUploadCompleteToBlob(componentComplete.iconFileUploadComplete, skipArrayBufferConversion);
      } else if (componentComplete.iconFileUploadComplete.fileUpload?.filePathCode) {
        // Fallback to remote URL
        let filePathCode = componentComplete.iconFileUploadComplete.fileUpload.filePathCode;
        componentComplete.iconFileUploadComplete.srcUrl = `${connLink}/getFileFromGetRequest/${filePathCode}`;
      }
    }
  }

  updateComponentLocallyWhenValueChanged(component: Component, updatedComponent: Component) {
    component.isOn = updatedComponent.isOn;
    component.currentValue = updatedComponent.currentValue;
    component.isOptimalValue = updatedComponent.isOptimalValue;
    component.lastValueUpdateAt = updatedComponent.lastValueUpdateAt || new Date().toISOString();
  }



  getAdminComponents(labComplete: LabComplete) {
    let componentsComplete: ComponentComplete[] = []
    for (let i = 0; i < labComplete.gadgetsComplete.length; i++) {
      let gadgetComplete = labComplete.gadgetsComplete[i];
      console.log(gadgetComplete);

      if (gadgetComplete.currentGadgetUserComplete.gadgetUser.roleId <= RoleEnum.CREATOR) {
        for (let j = 0; j < gadgetComplete.componentsComplete.length; j++) {
          componentsComplete.push(gadgetComplete.componentsComplete[j]);
        }
      }
    }
    console.log(componentsComplete)
    return componentsComplete
  }

  getLocalComponents(labComplete: LabComplete) {
    let componentsComplete: ComponentComplete[] = []
    for (let i = 0; i < labComplete.gadgetsComplete.length; i++) {
      let gadgetComplete = labComplete.gadgetsComplete[i];

      for (let j = 0; j < gadgetComplete.componentsComplete.length; j++) {
        componentsComplete.push(gadgetComplete.componentsComplete[j]);
      }
    }
    return componentsComplete
  }

  updateLabUserSettings(userAuthService: UserAuthService, labId: string, labUsersComplete: LabUserComplete[], labUserRequestsComplete: LabUserRequestComplete[]) {
    return new Promise<LabUserCombo>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();
      this.http.post(connLink + "/updateLabUserSettings", { uid: userAuthService.currentUserComplete.user.authUid, idToken, labId, labUsersComplete, labUserRequestsComplete }).subscribe(
        (response: any) => {

          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          reject();
        }
      )
    })
  }

  private normalizeLabControllerPermissions(labController: LabController) {
    if (!labController) {
      return;
    }
    if (!labController.usePermission) {
      labController.usePermission = LabControllerUsePermissionEnum.ONLY_ME;
    }
    if (!labController.editPermission) {
      labController.editPermission = labController.usePermission === LabControllerUsePermissionEnum.ONLY_ME ? LabControllerEditPermissionEnum.ONLY_ME : LabControllerEditPermissionEnum.GADGET_ADMIN;
    }
    if (labController.usePermission === LabControllerUsePermissionEnum.ONLY_ME) {
      labController.editPermission = LabControllerEditPermissionEnum.ONLY_ME;
    }
  }

  private userHasGadgetAdminAccessForLab(labComplete: LabComplete, currentUserId: string) {
    if (!labComplete) {
      return false;
    }
    for (let i = 0; i < labComplete.gadgetsComplete.length; i++) {
      const gadgetComplete = labComplete.gadgetsComplete[i];
      const currentGadgetUser = gadgetComplete.currentGadgetUserComplete?.gadgetUser;
      if (currentGadgetUser && currentGadgetUser.userId === currentUserId && currentGadgetUser.roleId <= RoleEnum.ADMIN) {
        return true;
      }
    }
    return false;
  }

  private canUserUseLabController(labController: LabController, currentUserId: string, userRoleId?: RoleEnum, hasGadgetAdminAccess?: boolean) {
    if (!labController) {
      return false;
    }
    this.normalizeLabControllerPermissions(labController);
    if (labController.userId === currentUserId) {
      return true;
    }
    if (labController.usePermission === LabControllerUsePermissionEnum.EVERYONE) {
      return userRoleId !== undefined;
    }
    if (labController.usePermission === LabControllerUsePermissionEnum.GADGET_ADMIN) {
      return !!hasGadgetAdminAccess;
    }
    return false;
  }

  refreshLabControllersForLab(labId: string, userAuthService: UserAuthService) {
    const currentUserId = userAuthService.currentUserComplete.user.id;

    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];
      if (labComplete.lab.id === labId) {
        const labRoleId = labComplete.currentLabUserComplete?.labUser?.roleId;
        const hasGadgetAdminAccess = this.userHasGadgetAdminAccessForLab(labComplete, currentUserId);

        // Fetch current controllers from backend to get any new ones user can now access
        userAuthService.createIdToken().then((idToken) => {
          const languageId = userAuthService.currentUserComplete.user.lastLanguageId || LanguageEnum.ENGLISH;

          this.http.post(connLink + "/getLabControllersCompleteByLabId", {
            uid: userAuthService.currentUserComplete.user.authUid,
            idToken,
            labId,
            languageId
          }).subscribe(
            (response: any) => {
              const labControllersComplete: LabControllerComplete[] = response as LabControllerComplete[];

              // Get list of controller IDs we currently have
              const currentControllerIds = new Set(
                labComplete.labControllersComplete.map(ctrl => ctrl.labController.id)
              );

              // Get list of controller IDs from backend
              const backendControllerIds = new Set(
                labControllersComplete.map(ctrl => ctrl.labController.id)
              );

              // Remove controllers that are no longer accessible
              for (let j = labComplete.labControllersComplete.length - 1; j >= 0; j--) {
                let labControllerComplete = labComplete.labControllersComplete[j];
                this.normalizeLabControllerPermissions(labControllerComplete.labController);
                const canUseController = this.canUserUseLabController(
                  labControllerComplete.labController,
                  currentUserId,
                  labRoleId,
                  hasGadgetAdminAccess
                );

                if (!canUseController || !backendControllerIds.has(labControllerComplete.labController.id)) {
                  // Remove controller the user can no longer access
                  const removedController = labComplete.labControllersComplete.splice(j, 1)[0];
                  this.labControllerCompleteChanged.next(removedController);
                }
              }

              // Add new controllers that user can now access
              for (let k = 0; k < labControllersComplete.length; k++) {
                const newController = labControllersComplete[k];
                this.normalizeLabControllerPermissions(newController.labController);

                if (!currentControllerIds.has(newController.labController.id)) {
                  // This is a new controller the user can now access
                  labComplete.labControllersComplete.push(newController);
                  this.labControllerCompleteChanged.next(newController);
                }
              }
            },
            (error) => {
              console.log("Error fetching lab controllers:", error);
              // Fallback to just removing inaccessible ones
              for (let j = labComplete.labControllersComplete.length - 1; j >= 0; j--) {
                let labControllerComplete = labComplete.labControllersComplete[j];
                this.normalizeLabControllerPermissions(labControllerComplete.labController);
                const canUseController = this.canUserUseLabController(
                  labControllerComplete.labController,
                  currentUserId,
                  labRoleId,
                  hasGadgetAdminAccess
                );

                if (!canUseController) {
                  const removedController = labComplete.labControllersComplete.splice(j, 1)[0];
                  this.labControllerCompleteChanged.next(removedController);
                }
              }
            }
          );
        }).catch((error) => {
          console.log("Error in refreshLabControllersForLab:", error);
        });
        break;
      }
    }
  }

  updateLabControllerCompleteLocally(updatedLabControllerComplete: LabControllerComplete, userAuthService: UserAuthService) {
    const currentUserId = userAuthService.currentUserComplete.user.id;
    const updatedLabController = updatedLabControllerComplete.labController;
    this.normalizeLabControllerPermissions(updatedLabController);
    let foundLabController = false;
    let removedLabController = false;

    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];
      for (let j = 0; j < labComplete.labControllersComplete.length; j++) {
        let labControllerComplete = labComplete.labControllersComplete[j];
        this.normalizeLabControllerPermissions(labControllerComplete.labController);
        if (labControllerComplete.labController.id === updatedLabController.id) {
          const labRoleId = labComplete.currentLabUserComplete?.labUser?.roleId;
          const hasGadgetAdminAccess = this.userHasGadgetAdminAccessForLab(labComplete, currentUserId);
          const canUseUpdatedController = this.canUserUseLabController(updatedLabController, currentUserId, labRoleId, hasGadgetAdminAccess);
          if (!canUseUpdatedController) {
            labComplete.labControllersComplete.splice(j, 1);
            this.labControllerCompleteChanged.next(labControllerComplete);
            removedLabController = true;
          } else {
            // Otherwise, update it
            updateObjKeepingRef(labControllerComplete, updatedLabControllerComplete);
            this.labControllerCompleteChanged.next(labControllerComplete);
          }
          foundLabController = true;
          break;
        }
      }
      // If we removed or updated, stop searching
      if (foundLabController || removedLabController) {
        break;
      }
    }
    // If not found and should be added (owned by user or public)
    if (!foundLabController && !removedLabController) {
      // Find the correct labComplete to add to
      for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
        let labComplete = userAuthService.currentUserComplete.labsComplete[i];
        if (labComplete.lab.id === updatedLabController.labId) {
          const labRoleId = labComplete.currentLabUserComplete?.labUser?.roleId;
          const hasGadgetAdminAccess = this.userHasGadgetAdminAccessForLab(labComplete, currentUserId);
          if (this.canUserUseLabController(updatedLabController, currentUserId, labRoleId, hasGadgetAdminAccess)) {
            labComplete.labControllersComplete.push(updatedLabControllerComplete);
          }
          break;
        }
      }
    }
  }

  upsertLabUsersCompleteLocally(newLabUsersComplete: LabUserComplete[], userAuthService: UserAuthService, gadgetService: GadgetService) {
    if (!newLabUsersComplete || newLabUsersComplete.length === 0) return;

    let labId = newLabUsersComplete[0].labUser.labId;

    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];
      if (labComplete.lab.id == labId) {

        for (let k = 0; k < newLabUsersComplete.length; k++) {
          let newUserComplete = newLabUsersComplete[k];
          let existingIndex = -1;

          for (let j = 0; j < labComplete.labUsersComplete.length; j++) {
            if (labComplete.labUsersComplete[j].labUser.userId == newUserComplete.labUser.userId) {
              existingIndex = j;
              break;
            }
          }

          if (existingIndex > -1) {
            // Update existing user
            labComplete.labUsersComplete[existingIndex] = newUserComplete;

            // Check if this is the currently selected user and update reference
            if (this.currentSelectedLab && this.currentSelectedLab.lab.id == labId &&
              this.currentSelectedLab.currentLabUserComplete &&
              this.currentSelectedLab.currentLabUserComplete.labUser.userId == newUserComplete.labUser.userId) {

              this.currentSelectedLab.currentLabUserComplete = newUserComplete;
              this.currentSelectedLabUserSettingsChanged.next(this.currentSelectedLab.currentLabUserComplete);
            }

          } else {
            // Append new user
            labComplete.labUsersComplete.push(newUserComplete);
          }

          // Upsert Gadget Users (Bundled)
          if (newUserComplete.gadgetUsersComplete && newUserComplete.gadgetUsersComplete.length > 0) {
            gadgetService.upsertGadgetUsersCompleteLocally(newUserComplete.gadgetUsersComplete, userAuthService);
          }
        }
        break;
      }
    }
  }

  deleteLabUsersLocally(labId: string, deletedUserIds: string[], userAuthService: UserAuthService) {
    if (!deletedUserIds || deletedUserIds.length === 0) return;

    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];
      if (labComplete.lab.id == labId) {

        // Check if CURRENT USER is removed
        if (deletedUserIds.includes(userAuthService.currentUserComplete.user.id)) {
          this.currentSelectedLabUserRemoved.next({ labId: labId, message: "You were removed from the lab." });
          userAuthService.currentUserComplete.labsComplete.splice(i, 1);
          return; // Lab removed, stop processing
        }

        // Remove from Lab Users List
        for (let j = 0; j < deletedUserIds.length; j++) {
          let userIdToDelete = deletedUserIds[j];

          for (let k = 0; k < labComplete.labUsersComplete.length; k++) {
            if (labComplete.labUsersComplete[k].labUser.userId == userIdToDelete) {
              labComplete.labUsersComplete.splice(k, 1);
              k--;
            }
          }

          // Remove from ALL Gadgets (Cascading Delete)
          if (labComplete.gadgetsComplete) {
            for (let g = 0; g < labComplete.gadgetsComplete.length; g++) {
              let gadget = labComplete.gadgetsComplete[g];
              if (gadget.gadgetUsersComplete) {
                for (let u = 0; u < gadget.gadgetUsersComplete.length; u++) {
                  if (gadget.gadgetUsersComplete[u].gadgetUser.userId == userIdToDelete) {
                    gadget.gadgetUsersComplete.splice(u, 1);
                    u--;
                  }
                }
              }
            }
          }
        }
        break;
      }
    }
  }

  gotNewLabComplete(labComplete: LabComplete, userAuthService: UserAuthService, gadgetService: GadgetService) {
    // Check if lab already exists to prevent duplicates
    const labExists = userAuthService.currentUserComplete.labsComplete.some(
      existingLab => existingLab.lab.id === labComplete.lab.id
    );

    if (labExists) {
      console.log('[LabService] Lab already exists in user labs, skipping duplicate addition:', labComplete.lab.id);
      return;
    }

    this.convertLabImages([labComplete], gadgetService);
    userAuthService.currentUserComplete.labsComplete.push(labComplete);
    // Join the new lab room since user just joined/created this lab
    this.liveUpdatesService.joinRoom(IoRoomHeaderEnum.LAB, labComplete.lab.id);
  }

  convertLabImages(labsComplete: LabComplete[], gadgetService: GadgetService) {
    for (let i = 0; i < labsComplete.length; i++) {
      let labComplete = labsComplete[i];
      this.convertLabImageBuffersToBlob(labComplete)

      for (let j = 0; j < labComplete.gadgetsComplete.length; j++) {
        let gadgetComplete = labComplete.gadgetsComplete[j];
        gadgetService.convertGadgetImageBuffersToBlob(gadgetComplete);

        for (let k = 0; k < gadgetComplete.componentsComplete.length; k++) {
          let componentComplete = gadgetComplete.componentsComplete[k];
          this.convertComponentImageBuffersToBlob(componentComplete);
        }
      }
    }
  }

  leaveLabRoom(labId: string) {
    this.liveUpdatesService.leaveRoom(labId);
  }

  createLab(userAuthService: UserAuthService, labName: string, languageId: LanguageEnum, iconFileUploadComplete: FileUploadComplete, curatedIconUrl?: string) {
    return new Promise<LabComplete>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();

      const formData = new FormData();
      formData.append("uid", userAuthService.currentUserComplete.user.authUid);
      formData.append("labName", labName)
      formData.append("languageId", languageId.toString());
      formData.append("idToken", idToken.toString());

      if (iconFileUploadComplete) {
        formData.append("files", iconFileUploadComplete.file, iconFileUploadComplete.file.name);
      }

      if (curatedIconUrl) {
        formData.append("curatedIconUrl", curatedIconUrl);
      }

      this.http.post(connLink + "/createLab", formData, {}).subscribe(
        (response: any) => {
          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          reject();
        }
      )
    })
  }

  quickJoinLab(labCode: string, userAuthService: UserAuthService, languageId: LanguageEnum) {
    return new Promise<LabComplete>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();
      this.http.post(connLink + "/quickJoinLab", { uid: userAuthService.currentUserComplete.user.authUid, idToken, labCode, languageId }).subscribe(
        (response: any) => {
          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          if (failedResponse.genericErrorCode) {
            if (failedResponse.genericErrorCode == GenericErrorCode.DataNotFound) {
              presentSnackBar("A lab under this code was not found with quick join enabled!", SnackBarColor.DANGER, this.matSnackBar);
            }
            else if (failedResponse.genericErrorCode == GenericErrorCode.PreventingDuplicateData) {
              presentSnackBar("You have already joined this lab!", SnackBarColor.DANGER, this.matSnackBar);
            }
          }
          reject();
        }
      )
    })
  }

  createGadget(userAuthService: UserAuthService, gadgetName: string, languageId: LanguageEnum, labId: string, iconFileUploadComplete: FileUploadComplete, curatedIconUrl?: string) {
    return new Promise<GadgetComplete>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();
      const formData = new FormData();
      formData.append("uid", userAuthService.currentUserComplete.user.authUid);
      formData.append("gadgetName", gadgetName)
      formData.append("languageId", languageId.toString());
      formData.append("idToken", idToken.toString());
      formData.append("labId", labId.toString());

      if (iconFileUploadComplete) {
        formData.append("files", iconFileUploadComplete.file, iconFileUploadComplete.file.name);
      }

      if (curatedIconUrl) {
        formData.append("curatedIconUrl", curatedIconUrl);
      }
      this.http.post(connLink + "/createGadget", formData).subscribe(
        (response: any) => {
          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          reject();
        }
      )
    })
  }

  updateComponentSettings(userAuthService: UserAuthService, componentComplete: ComponentComplete, labId: string) {
    return new Promise<ComponentSettingsReturn>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();


      const formData = new FormData();
      formData.append("labId", labId);
      formData.append("componentComplete", JSON.stringify(componentComplete))
      formData.append("uid", userAuthService.currentUserComplete.user.authUid);
      formData.append("idToken", idToken.toString());

      if (componentComplete.iconFileUploadComplete && componentComplete.iconFileUploadComplete.file) {
        formData.append("files", componentComplete.iconFileUploadComplete.file, componentComplete.iconFileUploadComplete.file.name);
      }


      this.http.post(connLink + "/updateComponentSettings", formData, {}).subscribe(
        (response: any) => {
          console.log("success")
          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          reject();
        }
      )
    })
  }

  getComponentValueHistory(componentId: string, userAuthService: UserAuthService) {
    return new Promise<ComponentValueHistory[]>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();
      this.http.post(connLink + "/getComponentValueHistory", { uid: userAuthService.currentUserComplete.user.authUid, idToken, componentId }).subscribe(
        (response: any) => {
          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          reject();
        }
      )
    })
  }

  getComponentValueHistoryPage(componentId: string, userAuthService: UserAuthService, options?: {
    limit?: number;
    offset?: number;
    actorQuery?: string;
    dateFrom?: string | null;
    dateTo?: string | null;
  }) {
    return new Promise<ComponentValueHistoryPage>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();
      this.http.post(connLink + "/getComponentValueHistory", {
        uid: userAuthService.currentUserComplete.user.authUid,
        idToken,
        componentId,
        paginated: true,
        limit: options?.limit,
        offset: options?.offset,
        actorQuery: options?.actorQuery,
        dateFrom: options?.dateFrom,
        dateTo: options?.dateTo,
      }).subscribe(
        (response: any) => {
          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          reject();
        }
      )
    })
  }

  deleteLabLocally(userAuthService: UserAuthService, labId: string) {
    console.log('[LabService] deleteLabLocally called for labId:', labId);
    console.log('[LabService] Call stack:', new Error().stack);

    // Safety check: Don't process if user is not logged in (prevents false positives during login/logout)
    if (!userAuthService.loggedIn || !userAuthService.currentUserComplete) {
      console.log('[LabService] User not logged in, skipping lab deletion to prevent false positives');
      return;
    }

    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];
      if (labComplete.lab.id == labId) {
        if (this.currentSelectedLab && this.currentSelectedLab.lab.id == labId) {
          console.log('[LabService] Clearing current selected lab and emitting removal event');

          // Emit removal event before clearing
          this.currentSelectedLabUserRemoved.next({
            labId: labId,
            message: 'You were removed from this lab'
          });

          this.currentSelectedLab = null;
          this.currentSelectedComponent = null;
          this.currentSelectedGadget = null;
          this.stateService.triggerMainSliderSectionObservable(MainSliderSection.LABS, true);
          // Room leaving is handled by app.component.ts when it receives the removal event
        }
        userAuthService.currentUserComplete.labsComplete.splice(i, 1);
        break;
      }
    }
  }

  deleteLab(userAuthService: UserAuthService, labId: string) {
    return new Promise<void>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();
      this.http.post(connLink + "/deleteLab", { uid: userAuthService.currentUserComplete.user.authUid, idToken, labId }).subscribe(
        (response: any) => {
          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          reject();
        }
      )
    })
  }

  createGadgetComponent(userAuthService: UserAuthService, componentName: string, languageId: LanguageEnum, gadgetId: string, iconFileUploadComplete: FileUploadComplete, curatedIconUrl?: string) {
    return new Promise<ComponentSettingsReturn>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();

      const formData = new FormData();
      formData.append("languageId", languageId.toString());
      formData.append("componentName", componentName)
      formData.append("uid", userAuthService.currentUserComplete.user.authUid);
      formData.append("idToken", idToken.toString());
      formData.append("gadgetId", gadgetId);

      if (iconFileUploadComplete && iconFileUploadComplete.file) {
        formData.append("files", iconFileUploadComplete.file, iconFileUploadComplete.file.name);
      }

      if (curatedIconUrl) {
        formData.append("curatedIconUrl", curatedIconUrl);
      }

      this.http.post(connLink + "/createComponent", formData).subscribe(
        (response: any) => {
          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          reject();
        }
      )
    })
  }



  //We can only edit other lab users if we have a higher role
  canUserEditOtherLabUser(personalLabUser: LabUser, otherLabUser: LabUser, canEditIfUserIdsMatch: boolean = true) {
    let pass = false;
    if (personalLabUser.userId == otherLabUser.userId && canEditIfUserIdsMatch) {
      pass = true;
    }
    //There should never be two owners, but this is a failsafe
    else if (personalLabUser.roleId == RoleEnum.OWNER && otherLabUser.roleId != RoleEnum.OWNER) {
      pass = true;
    }
    else if (personalLabUser.roleId <= RoleEnum.ADMIN && otherLabUser.roleId > RoleEnum.ADMIN && otherLabUser.roleId != RoleEnum.OWNER) {

      pass = true;
    }
    else if (personalLabUser.roleId < otherLabUser.roleId) {
      pass = true;
    }

    return pass;
  }

  acceptLabUserRequest(userAuthService: UserAuthService, languageId: LanguageEnum, labUserRequestId: string, gadgetService: GadgetService) {
    return new Promise<LabComplete>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();
      this.http.post(connLink + "/acceptLabUserRequest", { uid: userAuthService.currentUserComplete.user.authUid, idToken, labUserRequestId, languageId }).subscribe(
        (response: any) => {
          let labComplete: LabComplete = response;
          userAuthService.currentUserComplete.labsComplete.push(labComplete);
          this.liveUpdatesService.joinRoom(IoRoomHeaderEnum.LAB, labComplete.lab.id);

          this.convertLabImages([labComplete], gadgetService);
          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          reject();
        }
      )
    })
  }

  deleteLabUserRequest(userAuthService: UserAuthService, labUserRequestId: string) {
    return new Promise<void>(async (resolve, reject) => {
      let idToken = await userAuthService.createIdToken();
      this.http.post(connLink + "/deleteLabUserRequest", { uid: userAuthService.currentUserComplete.user.authUid, idToken, labUserRequestId }).subscribe(
        (response: any) => {
          resolve(response);
        },
        (response: any) => {
          let failedResponse: FailedResponse = response.error;
          console.log("failed response", failedResponse);
          if (failedResponse.apiErrorCode) {
            presentSnackBar(failedResponse.apiErrorCode, SnackBarColor.DANGER, this.matSnackBar);
          }
          reject();
        }
      )
    })
  }

  calculateNextScheduleDate(componentSchedule: ComponentSchedule) {

    if (componentSchedule.frequencyAmount >= 1) {
      let finished = false;

      let nextDate: Date;
      let todaysDate: Date = new Date();

      if (componentSchedule.nextDate == null || componentSchedule.nextDate == "") {
        nextDate = new Date();
      }
      else {
        nextDate = convertUTCStringToLocalDate(componentSchedule.nextDate);
      }

      let frequencyAmt = componentSchedule.frequencyAmount;

      nextDate.setFullYear(todaysDate.getFullYear());

      while (!finished) {
        if (componentSchedule.frequencyType == DateFrequencyEnum.MINUTES) {
          nextDate.setDate(todaysDate.getDate());
          nextDate.setMonth(todaysDate.getMonth());
          nextDate.setMinutes(nextDate.getMinutes() + componentSchedule.frequencyAmount);
        }
        else if (componentSchedule.frequencyType == DateFrequencyEnum.HOURS) {
          nextDate.setDate(todaysDate.getDate());
          nextDate.setMonth(todaysDate.getMonth());
          nextDate.setHours(nextDate.getHours() + componentSchedule.frequencyAmount);
        }
        else if (componentSchedule.frequencyType == DateFrequencyEnum.DAYS) {
          nextDate.setMonth(todaysDate.getMonth());
          nextDate.setDate(nextDate.getDate() + frequencyAmt);
        }
        else if (componentSchedule.frequencyType == DateFrequencyEnum.WEEKS) {

          if (componentSchedule.frequencyDaysOfWeek.length == 0) {
            nextDate.setDate(nextDate.getDate() + frequencyAmt * 7);
          }
          else {
            let weekDaysStrings: string[] = componentSchedule.frequencyDaysOfWeek.split(":");
            weekDaysStrings.splice(0, 1);

            let weekDays: number[] = [];
            for (let i = 0; i < weekDaysStrings.length; i++) {
              weekDays.push(Number(weekDaysStrings[i]))
            }
            weekDays = weekDays.sort();

            let currentWeekday = nextDate.getDay();
            let newWeek = true;
            let addedDays: number = 0;
            for (let i = 0; i < weekDays.length; i++) {
              let weekday: number = Number(weekDays[i]);
              if (weekday > currentWeekday) {
                addedDays = weekday - currentWeekday;
                newWeek = false;
                break;
              }
            }
            if (newWeek == true) {
              let daysOfWeek = 7;
              addedDays = daysOfWeek - (currentWeekday - Number(weekDays[0]));

              addedDays += 7 * (frequencyAmt - 1);
            }

            nextDate.setDate(nextDate.getDate() + addedDays)

          }
        }
        else if (componentSchedule.frequencyType == DateFrequencyEnum.MONTHS) {

          let monthDaysStrings: string[] = componentSchedule.frequencyDaysOfMonth.split("::");
          let monthDays: number[] = [];


          //Order month days array
          for (let i = 0; i < monthDaysStrings.length; i++) {
            monthDaysStrings[i] = monthDaysStrings[i].replace(":", "").replace(":", "")
            if (monthDaysStrings[i] == "") {
              monthDaysStrings.splice(i, 1);
              i--;
            }
            else {
              monthDays.push(Number(monthDaysStrings[i]));
            }
          }
          monthDays = monthDays.sort();

          let currentMonthDay = nextDate.getDate();
          let currentMonthTotalDays = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate();

          let newMonth = true;
          let targetMonthDay = null;


          //Check if we are moving to another selected date this month without changing months
          for (let i = 0; i < monthDays.length; i++) {
            let targetMonthDay: number = Number(monthDays[i]) + 1;

            if (targetMonthDay > currentMonthTotalDays) {
              if (componentSchedule.useLastDayOfMonth) {
                targetMonthDay = currentMonthTotalDays;
              }
              else {
                break;
              }
            }

            if (currentMonthDay < targetMonthDay) {
              let addedDays: number = targetMonthDay - currentMonthDay;
              nextDate.setDate(nextDate.getDate() + addedDays)
              newMonth = false;
              break;
            }

          }

          if (newMonth == true) {

            if (targetMonthDay == null) {
              targetMonthDay = monthDays[0] + 1;
            }

            let addedMonths = componentSchedule.frequencyAmount;

            while (true) {
              let targetMonthTotalDays = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1 + addedMonths, 0).getDate();
              if (targetMonthDay > targetMonthTotalDays && componentSchedule.useLastDayOfMonth == false) {
                addedMonths++;
              }
              else {
                break;
              }
            }

            let targetMonthTotalDays = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1 + addedMonths, 0).getDate();
            let month = nextDate.getMonth() + addedMonths;
            let date = targetMonthDay > targetMonthTotalDays ? targetMonthTotalDays : targetMonthDay;
            nextDate = new Date(nextDate.getFullYear(), month, date, nextDate.getHours(), nextDate.getMinutes(), 0);

          }

        }
        if (nextDate > new Date()) {
          finished = true;
        }
        componentSchedule.nextDate = convertLocalDateToUTCMySQLString(nextDate);
      }

    }

  }

  getLabComponentCompleteById(componentId: string) {
    for (let i = 0; i < this.currentSelectedLab.gadgetsComplete.length; i++) {
      let gadgetComplete = this.currentSelectedLab.gadgetsComplete[i];

      for (let j = 0; j < gadgetComplete.componentsComplete.length; j++) {

        let componentComplete = gadgetComplete.componentsComplete[j];
        if (componentComplete.component.id == componentId) {
          return componentComplete;
        }
      }
    }
    return null
  }

  subscribeToLabCompleteIO(labId: string) {

  }

  removeLabUserRequestLocally(labId: string, labUserRequestId: string, userAuthService: UserAuthService) {
    console.log('[LabService] removeLabUserRequestLocally called with:', { labId, labUserRequestId });
    console.log('[LabService] Current user labs count:', userAuthService.currentUserComplete.labsComplete.length);

    // Find the lab in the current user's labs
    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];
      console.log('[LabService] Checking lab:', { labId: labComplete.lab.id, matches: labComplete.lab.id === labId });

      if (labComplete.lab.id === labId) {
        console.log('[LabService] Found matching lab, current labUserRequestsComplete count:', labComplete.labUserRequestsComplete?.length || 0);

        // Remove the lab user request from the lab's requests
        if (labComplete.labUserRequestsComplete) {
          const initialCount = labComplete.labUserRequestsComplete.length;
          labComplete.labUserRequestsComplete = labComplete.labUserRequestsComplete.filter(
            request => request.labUserRequest.id !== labUserRequestId
          );
          const finalCount = labComplete.labUserRequestsComplete.length;
          console.log('[LabService] Filtered labUserRequestsComplete:', { initialCount, finalCount, removed: initialCount - finalCount });
        }

        // Emit event for components that need to update their local state
        console.log('[LabService] Emitting currentSelectedLabUserRequestDeleted event');
        this.currentSelectedLabUserRequestDeleted.next({
          labUserRequestId: labUserRequestId,
          labId: labId
        });
        console.log('[LabService] Event emitted successfully');

        break;
      }
    }

    console.log('[LabService] removeLabUserRequestLocally completed');
  }

  removeLabUserRequestsForUserId(labId: string, userId: string, userAuthService: UserAuthService) {
    console.log('[LabService] removeLabUserRequestsForUserId called with:', { labId, userId });

    // Find the lab in the current user's labs
    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];

      if (labComplete.lab.id === labId) {
        console.log('[LabService] Found matching lab, removing lab user requests for userId:', userId);

        // Remove any lab user requests for this user ID
        if (labComplete.labUserRequestsComplete) {
          const initialCount = labComplete.labUserRequestsComplete.length;
          labComplete.labUserRequestsComplete = labComplete.labUserRequestsComplete.filter(
            request => request.labUserRequest.userId !== userId
          );
          const finalCount = labComplete.labUserRequestsComplete.length;
          const removedCount = initialCount - finalCount;

          console.log('[LabService] Removed lab user requests for userId:', {
            userId,
            initialCount,
            finalCount,
            removedCount
          });

          // If any requests were removed, emit the event for components to update
          if (removedCount > 0) {
            console.log('[LabService] Emitting currentSelectedLabUserRequestDeleted event for removed requests');
            this.currentSelectedLabUserRequestDeleted.next({
              labUserRequestId: 'multiple', // Indicates multiple requests were removed
              labId: labId,
              userId: userId,
              removedCount: removedCount
            });
          }
        }

        break;
      }
    }

    console.log('[LabService] removeLabUserRequestsForUserId completed');
  }

  checkIfCurrentUserRemovedFromLab(labId: string, userAuthService: UserAuthService): boolean {
    console.log('[LabService] checkIfCurrentUserRemovedFromLab called for labId:', labId);
    console.log('[LabService] Current user ID:', userAuthService.currentUserComplete.user.id);

    // Check if the current user is still in this lab
    for (let i = 0; i < userAuthService.currentUserComplete.labsComplete.length; i++) {
      let labComplete = userAuthService.currentUserComplete.labsComplete[i];

      if (labComplete.lab.id === labId) {
        // Check if the current user exists in this lab
        const userExists = labComplete.labUsersComplete.some(
          labUser => labUser.labUser.userId === userAuthService.currentUserComplete.user.id
        );

        console.log('[LabService] User exists in lab:', userExists);
        return !userExists; // Return true if user was removed
      }
    }

    // If lab not found, user was removed
    console.log('[LabService] Lab not found, user was removed');
    return true;
  }

  /**
   * Clear all lab service data on logout to prevent stale data issues
   */
  clearAllData(): void {
    console.log('[LabService] Clearing all lab service data');
    console.log('[LabService] Before clear - currentSelectedLab:', this.currentSelectedLab?.labLanguage?.name || this.currentSelectedLab?.lab?.id || 'null');

    // Clear selected items
    this.currentSelectedLab = null;
    this.currentSelectedGadget = null;
    this.currentSelectedComponent = null;

    // Note: We don't clear the Subject subscriptions here as they need to persist
    // for the next login session. The app.component.ts manages subscription lifecycle.

    console.log('[LabService] After clear - currentSelectedLab:', this.currentSelectedLab);
    console.log('[LabService] Lab service data cleared successfully');
  }





}
